import { router, useLocalSearchParams } from 'expo-router';
import { useEffect, useState } from 'react';
import { ActivityIndicator, View } from 'react-native';

import { Button, Text } from '@/components/ui';

import logoUrl from '../../assets/icon-only.png';

import { authClient } from '@/lib/auth/client';
import { useSession } from '@/lib/auth/session';
import { isNativePlatform } from '@/lib/auth/is-native';
import { signInWithGoogleNative } from '@/lib/auth/native-google-sign-in';
import { getFrontendBaseUrl } from '@/lib/env';

function formatAuthError(raw: string): string {
  return decodeURIComponent(raw).replaceAll('_', ' ').trim();
}

function authCallbackUrl(): string {
  return `${getFrontendBaseUrl()}/`;
}

function authErrorCallbackUrl(): string {
  return `${getFrontendBaseUrl()}/login`;
}

export default function LoginScreen() {
  const { error: errorParam } = useLocalSearchParams<{ error?: string }>();
  const { data: session, isPending, error: sessionError } = useSession();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (typeof errorParam === 'string' && errorParam.length > 0) {
      setError(formatAuthError(errorParam));
    }
  }, [errorParam]);

  useEffect(() => {
    if (!isPending && session) {
      router.replace('/app');
    }
  }, [isPending, session]);

  useEffect(() => {
    if (sessionError?.message) {
      setError(sessionError.message);
    }
  }, [sessionError]);

  async function onGoogleSignIn() {
    setLoading(true);
    setError(null);

    try {
      if (isNativePlatform()) {
        await signInWithGoogleNative();
        router.replace('/app');
        return;
      }

      const { error: signInError } = await authClient.signIn.social({
        provider: 'google',
        callbackURL: authCallbackUrl(),
        errorCallbackURL: authErrorCallbackUrl(),
      });

      if (signInError) {
        setError(signInError.message ?? 'Sign-in failed');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sign-in failed');
    } finally {
      setLoading(false);
    }
  }

  if (isPending) {
    return (
      <View className="flex-1 items-center justify-center bg-surface">
        <ActivityIndicator size="large" />
        <Text variant="caption" className="mt-4">
          Checking session…
        </Text>
      </View>
    );
  }

  return (
    <View className="flex-1 bg-surface px-8">
      <View className="flex-1 items-center justify-center">
        <View className="mb-10 w-full max-w-sm items-center">
          <img
            src={logoUrl}
            alt="couplespace"
            width={160}
            height={160}
            className="mb-4"
          />
          <Text variant="display" className="mb-2 w-auto text-center">
            couplespace
          </Text>
          <Text variant="body" muted className="text-center">
            Our family's digital space
          </Text>
        </View>

        <Button
          className="w-full max-w-sm"
          variant="primary"
          size="lg"
          label="Continue with Google"
          loading={loading}
          disabled={loading}
          onPress={() => void onGoogleSignIn()}
        />

        {error ? (
          <Text variant="caption" className="mt-4 text-center text-danger">
            {error}
          </Text>
        ) : null}
      </View>

      <Text variant="caption" muted className="mt-8 mb-8 text-center">
        To my love Ole
      </Text>
    </View>
  );
}
